import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Webhook, RefreshCw, XCircle, Clock, MessageCircle, Activity, Send, Loader2, TestTube, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { StatCard, LogEntry } from "../components/webhook-monitor/LogEntry";
import { FeedbackAnalyticsCard } from "../components/webhook-monitor/FeedbackAnalyticsCard";
import { HumanTakeoverCard } from "../components/webhook-monitor/HumanTakeoverCard";
import { EscalatedThreadsCard } from "../components/webhook-monitor/EscalatedThreadsCard";
import { TestAIDialog } from "../components/webhook-monitor/TestAIDialog";
import { CorrectionDialog } from "../components/webhook-monitor/CorrectionDialog";
import { ThreadHistoryDialog } from "../components/webhook-monitor/ThreadHistoryDialog";

const WebhookMonitorPage = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [escalatedThreads, setEscalatedThreads] = useState([]);
  const [takeoverThreads, setTakeoverThreads] = useState([]);
  const [threadHistoryOpen, setThreadHistoryOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadHistory, setThreadHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, logsRes, feedbackRes, escalatedRes, takeoverRes] = await Promise.all([
        axios.get(`${API}/webhook/hubspot/stats`, { withCredentials: true }),
        axios.get(`${API}/webhook/hubspot/logs?limit=50`, { withCredentials: true }),
        axios.get(`${API}/auto-responder/feedback/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/webhook/hubspot/escalated`, { withCredentials: true }).catch(() => ({ data: { escalated_threads: [] } })),
        axios.get(`${API}/webhook/hubspot/human-takeover`, { withCredentials: true }).catch(() => ({ data: { threads: [] } }))
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setFeedbackStats(feedbackRes.data);
      setEscalatedThreads(escalatedRes.data.escalated_threads || []);
      setTakeoverThreads(takeoverRes.data.threads || []);
    } catch (error) {
      toast.error("Failed to load webhook data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleOpenCorrection = (log) => {
    setSelectedLog(log);
    setCorrectionDialogOpen(true);
  };

  const viewThreadHistory = async (threadId) => {
    setLoadingHistory(true);
    setSelectedThread(threadId);
    setThreadHistoryOpen(true);
    
    try {
      const response = await axios.get(`${API}/webhook/hubspot/thread/${threadId}/history`, { withCredentials: true });
      setThreadHistory(response.data);
    } catch (error) {
      toast.error("Failed to load thread history");
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const resolveThread = async (threadId) => {
    try {
      await axios.delete(`${API}/webhook/hubspot/escalated/${threadId}`, { withCredentials: true });
      toast.success("Thread resolved - auto-responder re-enabled");
      setEscalatedThreads(prev => prev.filter(t => t.thread_id !== threadId));
      setThreadHistoryOpen(false);
    } catch (error) {
      toast.error("Failed to resolve thread");
    }
  };

  const resumeAiForThread = async (threadId) => {
    try {
      await axios.delete(`${API}/webhook/hubspot/human-takeover/${threadId}`, { withCredentials: true });
      toast.success("AI responder resumed for this thread");
      setTakeoverThreads(prev => prev.filter(t => t.thread_id !== threadId));
    } catch (error) {
      toast.error("Failed to resume AI for this thread");
    }
  };

  if (loading) {
    return (
      <Layout title="Webhook Monitor">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Webhook Monitor">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Webhook className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Live Chat Auto-Responder</h1>
              <p className="text-sm text-slate-500">Monitor HubSpot webhook events and AI responses</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/ai-training">
              <Button
                variant="outline"
                className="gap-2 border-violet-200 text-violet-600 hover:bg-violet-50"
                data-testid="ai-training-btn"
              >
                <BookOpen className="w-4 h-4" />
                AI Training
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(true)}
              className="gap-2"
              data-testid="test-webhook-btn"
            >
              <TestTube className="w-4 h-4" />
              Test AI Response
            </Button>
            <Button
              variant="outline"
              onClick={fetchData}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Webhook URL Info */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-medium text-blue-900">HubSpot Webhook URL</p>
                <code className="text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded mt-1 inline-block break-all" data-testid="webhook-url">
                  {API}/webhook/hubspot/conversation
                </code>
                <p className="text-xs text-blue-600 mt-2">
                  Configure this URL in your HubSpot Developer App → Webhooks → conversation.newMessage
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={MessageCircle}
              label="Total Events"
              value={stats.total_events}
              color="bg-slate-600"
            />
            <StatCard
              icon={Send}
              label="Responses Sent"
              value={stats.responses_sent}
              color="bg-emerald-500"
            />
            <StatCard
              icon={XCircle}
              label="Errors"
              value={stats.errors}
              color="bg-red-500"
            />
            <StatCard
              icon={Clock}
              label="Avg Response Time"
              value={`${stats.avg_processing_time_ms}ms`}
              subtext={`${stats.success_rate.toFixed(1)}% success rate`}
              color="bg-violet-500"
            />
          </div>
        )}
        
        <FeedbackAnalyticsCard feedbackStats={feedbackStats} />
        <HumanTakeoverCard takeoverThreads={takeoverThreads} onResume={resumeAiForThread} />
        <EscalatedThreadsCard escalatedThreads={escalatedThreads} onViewHistory={viewThreadHistory} onResolve={resolveThread} />

        {/* Logs */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Recent Webhook Events
            </CardTitle>
            <CardDescription>
              Click on an event to see message details and AI response
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12">
                <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No webhook events yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Events will appear here when customers send messages via HubSpot live chat
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-4">
                  {logs.map((log) => (
                    <LogEntry key={log.log_id} log={log} onFeedback={handleOpenCorrection} />
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <TestAIDialog open={testDialogOpen} onOpenChange={setTestDialogOpen} />
        <CorrectionDialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen} selectedLog={selectedLog} />
        <ThreadHistoryDialog
          open={threadHistoryOpen}
          onOpenChange={setThreadHistoryOpen}
          selectedThread={selectedThread}
          threadHistory={threadHistory}
          loadingHistory={loadingHistory}
          onResolve={resolveThread}
        />
      </div>
    </Layout>
  );
};

export default WebhookMonitorPage;
